import React, { Children, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import usePACMembershipData from 'hooks/usePACMembershipData';

import { styled } from '@mui/material/styles';
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { ChevronRight, KeyboardArrowDown } from '@mui/icons-material';

import { useGetFeature } from 'hooks/api/feature/useGetFeature';
import { useGetCertificate } from 'hooks/api/certificate/useGetCertificate';

import { alumniMenus, dashboardMenus, helpMenus, reportMenus } from '../static';
import AlumniVerification from './AlumniVerification';

// ---------------------------- styles ----------------------------

const ListItemIconComponent = ({ selected, ...props }) => (
  <ListItemIcon {...props} />
);

const ListItemTextComponent = ({ selected, ...props }) => (
  <ListItemText {...props} />
);

const ItemIcon = styled(ListItemIconComponent)(({ theme, selected }) => ({
  minWidth: 0,
  color: theme.palette[selected ? 'primaryBrandPurwadhika' : 'neutral500'].main,

  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
}));

const ItemText = styled(ListItemTextComponent)(({ theme, selected }) => ({
  span: {
    ...theme.typography[selected ? 'subheading4' : 'paragraph2'],
  },

  color: theme.palette[selected ? 'primaryBrandPurwadhika' : 'neutral300'].main,
}));

const ListItemBtn = styled(ListItemButton)(({ theme }) => ({
  padding: '12px 8px',
  borderRadius: '8px',
  marginBottom: '8px',

  '&:last-child': { marginBottom: 0 },

  '&.Mui-selected': {
    textShadow: `0 0 0.1px ${theme.palette.primaryBrandPurwadhika.main},
                 0 0 0.1px ${theme.palette.primaryBrandPurwadhika.main}`,
    backgroundColor: theme.palette.primary500.main,

    '&:hover': {
      backgroundColor: theme.palette.primary500.main,
    },
  },

  [theme.breakpoints.down('md')]: {
    padding: '8px',
    borderRadius: '4px',
  },
}));

const ItemTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption1,

  padding: '8px',
  textTransform: 'uppercase',
  lineHeight: '16px !important',
  color: theme.palette.neutral500.main,

  [theme.breakpoints.down('md')]: {
    ...theme.typography.metatag1,
  },
}));

const CircleBox = styled(Box)(({ theme }) => ({
  width: '24px',
  height: '24px',
  backgroundColor: theme.palette.error500.main,
  borderRadius: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const SubItemComponent = styled(Box)(({ theme, selected }) => ({
  color: theme.palette[selected ? 'primaryBrandPurwadhika' : 'neutral500'].main,
  display: 'flex',
  alignItems: 'center',
}));

const DotSubItem = styled(Box)(({ theme, subItemSelected }) => ({
  width: subItemSelected ? '6px' : '4px',
  height: subItemSelected ? '6px' : '4px',
  borderRadius: '100%',
  backgroundColor: theme.palette.neutral500.main,
}));

const SubItemTitle = styled(Typography)(({ theme, subItemSelected }) => ({
  color:
    theme.palette[subItemSelected ? 'primaryBrandPurwadhika' : 'neutral300']
      .main,
}));

// ---------------------------- components ----------------------------

const LogoPwd = () => {
  const handleOnClick = () => {
    window.location.href = window.location.origin;
  };

  return (
    <Stack px={1} height="72px" justifyContent="center" onClick={handleOnClick}>
      <Box
        component="img"
        alt="Purwadhika Logo"
        src="/static/brand/logopwdk-text.png"
        sx={{ width: '156px', height: '24px' }}
      />
    </Stack>
  );
};

const ListItem = ({ data, onMenuClick }) => {
  const [openSubMenu, setOpenSubMenu] = useState(false);

  const { pathname } = useLocation();
  const { data: certificates } = useGetCertificate();
  const { data: alumniProfileDisabled } = useGetFeature('alumni-profile');
  const { data: jobPostingDisabled } = useGetFeature('job-posting');
  const { data: networkingDisabled } = useGetFeature('networking');

  return React.Children.toArray(
    Object.values(data).map(({ icon, label, path, subItem }) => {
      const isSubItemSelected =
        subItem && subItem.some((item) => item.path === pathname);
      const selected = isSubItemSelected || path === pathname;

      if (alumniProfileDisabled && path === '/alumni-profile') {
        return null;
      }

      if (jobPostingDisabled && label === 'Jobs') {
        return null;
      }

      if (networkingDisabled && path === '/networking') {
        return null;
      }

      return (
        <>
          <ListItemBtn
            selected={selected}
            onClick={(e) => {
              onMenuClick({ e, path, hasSubItem: subItem });
              if (subItem) {
                setOpenSubMenu(!openSubMenu);
              }
            }}
          >
            <Stack direction="row" justifyContent="space-between" width="100%">
              <Stack spacing={2} direction="row" alignItems="center">
                <ItemIcon selected={selected}>{icon}</ItemIcon>
                <ItemText selected={selected} primary={label} />
              </Stack>
              {subItem && (
                <SubItemComponent selected={selected}>
                  {openSubMenu ? <KeyboardArrowDown /> : <ChevronRight />}
                </SubItemComponent>
              )}

              {label === 'Certificate' && certificates?.length > 0 && (
                <CircleBox>
                  <Typography typography="caption1" color="error300.main">
                    {certificates?.length}
                  </Typography>
                </CircleBox>
              )}
            </Stack>
          </ListItemBtn>
          {subItem && (
            <Collapse in={openSubMenu}>
              {Children.toArray(
                subItem.map((item) => {
                  const subItemSelected = item.path === pathname;
                  return (
                    <List component="div" disablePadding>
                      <ListItemButton
                        onClick={(e) => {
                          onMenuClick({ e, path: item.path });
                        }}
                      >
                        <Stack
                          spacing="4px"
                          direction="row"
                          alignItems="center"
                        >
                          <Box width="20px">
                            <DotSubItem subItemSelected={subItemSelected} />
                          </Box>
                          <SubItemTitle
                            subItemSelected={subItemSelected}
                            typography={
                              subItemSelected ? 'subheading4' : 'paragraph2'
                            }
                          >
                            {item.label}
                          </SubItemTitle>
                        </Stack>
                      </ListItemButton>
                    </List>
                  );
                }),
              )}
            </Collapse>
          )}
        </>
      );
    }),
  );
};

// ---------------------------- main component ----------------------------

const SidebarMenu = ({ setOpen, alumniData = [] }) => {
  const isMobile = useMediaQuery(({ breakpoints }) => breakpoints.down('md'));

  const navigate = useNavigate();

  const { userStatus } = usePACMembershipData();

  const { tokenChecked } = useSelector((state) => state.auth);

  const { isAlumniFlagLoading, data: alumniFlagDisabled } =
    useGetFeature('alumni');
  const { isDashboardContactUsLoading, data: dashboardContactUsFlagDisabled } =
    useGetFeature('dashboard-contact-us');
  const { isDashboardReportLoading, data: dashboardReportFlagDisabled } =
    useGetFeature('dashboard-report');

  const alumniMenusEnabled = useMemo(() => {
    return !alumniFlagDisabled && !isAlumniFlagLoading && tokenChecked;
  }, [alumniFlagDisabled, isAlumniFlagLoading, tokenChecked]);

  const contactUsMenuEnabled = useMemo(() => {
    return (
      !dashboardContactUsFlagDisabled &&
      !isDashboardContactUsLoading &&
      tokenChecked &&
      (userStatus.isStudent || userStatus.isAlumni)
    );
  }, [
    dashboardContactUsFlagDisabled,
    isDashboardContactUsLoading,
    tokenChecked,
    userStatus.isStudent,
    userStatus.isAlumni,
  ]);

  const reportMenuEnabled = useMemo(
    () =>
      !isDashboardReportLoading &&
      tokenChecked &&
      !dashboardReportFlagDisabled &&
      userStatus.isStudent,
    [
      dashboardReportFlagDisabled,
      isDashboardReportLoading,
      tokenChecked,
      userStatus.isStudent,
    ],
  );

  const helpMenusEnabler = useMemo(() => {
    let help = [];

    if (contactUsMenuEnabled) {
      help = [...help, ...helpMenus];
    }

    if (reportMenuEnabled) {
      help = [...help, ...reportMenus];
    }

    return help;
  }, [contactUsMenuEnabled, reportMenuEnabled]);

  const sidebarMenus = {
    dashboard: { ...dashboardMenus },
    ...(alumniMenusEnabled && { alumni: alumniMenus }),
    ...(helpMenusEnabler.length > 0 && { help: helpMenusEnabler }),
    // ...(contactUsMenuEnabled && { help: helpMenus }),
    // ...(reportMenuEnabled && { help: reportMenus }),
  };

  const onMenuClick = ({ e, path, hasSubItem }) => {
    e.preventDefault();
    if (path) {
      navigate(path);
    }
    if (!hasSubItem) {
      setOpen(false);
    }
  };

  return (
    <Stack
      mx={{ xs: 1, md: 2 }}
      paddingTop={{ md: 2 }}
      paddingBottom={2}
      spacing={{ xs: 2, md: 3 }}
    >
      {isMobile && <LogoPwd />}

      {React.Children.toArray(
        Object.keys(sidebarMenus).map((key) => (
          <List component="nav" sx={{ p: 0 }}>
            <ItemTitle>{key}</ItemTitle>

            {key === 'alumni' && !userStatus.isEligibleForAlumniFeature ? (
              <AlumniVerification
                alumniData={alumniData}
                onMenuClick={onMenuClick}
              />
            ) : (
              <ListItem data={sidebarMenus[key]} onMenuClick={onMenuClick} />
            )}
          </List>
        )),
      )}
    </Stack>
  );
};

export default SidebarMenu;
